import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { RootState } from 'src/redux/rootReducerType';
import {
  setOpenHouseholdComposition,
  setOpenHouseholdCompositionForCertId,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { RENTER_PATHS } from 'src/routes/renterPaths';

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
  p: 2.5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1.5,
  textAlign: 'center',
};

/**
 * Blocking card shown when a renter without an authenticator app tries to
 * manage contact information (their own or another household member's).
 * Deep-links to the Account Hub 2FA setup, closing the household composition
 * modal first so it doesn't overlay the setup screen.
 */
const TwoFactorRequiredCard: FC<{ isSelf?: boolean }> = ({
  isSelf = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );

  const handleSetUp = useCallback((): void => {
    dispatch(setOpenHouseholdCompositionForCertId(null));
    dispatch(setOpenHouseholdComposition(false));
    navigate(
      `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${String(
        user?.id ?? ''
      )}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${
        RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA
      }/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA_SETUP}`
    );
  }, [dispatch, navigate, user?.id]);

  return (
    <Box sx={cardSx}>
      <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'primary.dark' }}>
        Two-Factor Authentication required
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 280 }}>
        {isSelf
          ? 'To edit your contact information, you must first set up an authenticator app on your account.'
          : 'To manage other users, you must first set up an authenticator app on your account.'}
      </Typography>
      <Button
        variant="contained"
        disableElevation
        size="small"
        startIcon={<SecurityIcon />}
        onClick={handleSetUp}
        sx={{ mt: 0.5, borderRadius: '8px', fontWeight: 600 }}
      >
        <span>Set up 2FA</span>
      </Button>
    </Box>
  );
};

export default TwoFactorRequiredCard;
